import { Combobox, ComboboxButton, ComboboxInput, ComboboxOption, ComboboxOptions, Label } from '@headlessui/react'
import clsx from 'clsx'
import { IconSvg } from 'components/common'
import { useState, useMemo, useCallback, memo, useRef, useEffect } from 'react'

type Option = {
	label: string
	sublabel?: string
	value: string
	icon?: React.ReactNode
}

export type SelectSearchBoxProps = {
	label?: string
	options: Option[]
	value: string | null
	onChange?: (value: string | null) => void
	placeholder?: string
	disabled?: boolean
	className?: string
}

export const SelectSearchBox: React.FC<SelectSearchBoxProps> = ({
	label,
	options,
	value,
	onChange,
	placeholder = 'Choose option',
	disabled,
	className,
}) => {
	const [inputWidth, setInputWidth] = useState<number>(0)
	const inputRef = useRef<HTMLDivElement>(null)

	const [query, setQuery] = useState('')

	useEffect(() => {
		if (inputRef.current) {
			setInputWidth(inputRef.current.offsetWidth)
		}
	}, [])

	const selectedOption = useMemo(() => {
		return options.find(opt => opt.value === value) || null
	}, [value, options])

	const filteredOptions = useMemo(() => {
		return query === ''
			? options
			: options.filter(option => option.label.toLowerCase().includes(query.toLowerCase()))
	}, [query, options])

	const handleChange = useCallback(
		(option: Option | null) => {
			setQuery('')
			onChange?.(option?.value || null)
		},
		[onChange]
	)

	const handleQueryChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		setQuery(event.target.value)
	}, [])

	const getDisplayValue = useCallback((option: Option | null) => option?.label ?? '', [])

	return (
		<Combobox as="div" value={selectedOption} onChange={handleChange} disabled={disabled}>
			<Label className="sr-only">{label || 'Choose your option'}</Label>
			<div className="relative w-full" ref={inputRef}>
				{selectedOption?.icon && (
					<span className="absolute inset-y-0 left-3 flex items-center pointer-events-none z-10">
						{selectedOption.icon}
					</span>
				)}
				<ComboboxInput
					className={clsx(
						'rounded-lg transition-all duration-300 border border-translucent-plus-border bg-primary-surface/[0.4] backdrop-blur-lg text-primary-text h-10 flex justify-start items-center space-x-1 min-w-[248px] group w-full pr-12 focus:ring-0 focus:border-translucent-plus-border',
						selectedOption?.icon ? 'pl-10' : 'px-4',
						className
					)}
					onChange={handleQueryChange}
					onBlur={() => setQuery('')}
					displayValue={getDisplayValue}
					placeholder={placeholder}
				/>
				<ComboboxButton className="absolute inset-y-0 right-0 flex items-center pr-4">
					{disabled ? (
						<IconSvg icon="lock" className="text-secondary-text" />
					) : (
						<IconSvg icon="dropdown" className="text-primary-icon" />
					)}
				</ComboboxButton>

				{filteredOptions.length > 0 && (
					<ComboboxOptions
						anchor={{ to: 'bottom', padding: '20px' }}
						style={{ width: inputWidth > 0 ? `${inputWidth}px` : 'auto' }}
						className="absolute z-10 mt-2 max-h-60 w-full overflow-auto rounded-lg bg-primary-surface/[0.4] backdrop-blur-lg py-2.5 text-base shadow-lg ring-1 ring-primary-border ring-opacity-5 focus:outline-none data-[closed]:data-[leave]:opacity-0 data-[leave]:transition data-[leave]:duration-100 data-[leave]:ease-in backdrop-blur-[32px]"
					>
						{filteredOptions.map(option => (
							<ComboboxOption
								key={option.value}
								value={option}
								className="group relative cursor-default select-none px-4 py-2.5 pl-3 pr-9 text-secondary-text data-[focus]:bg-secondary-surface/[0.2] backdrop-blur-lg data-[focus]:text-primary-text"
							>
								<div className="flex items-center">
									{option.icon && <span className="mr-2 shrink-0">{option.icon}</span>}
									<span className="truncate group-data-[selected]:font-semibold">{option.label}</span>
									{option.sublabel && (
										<span className="ml-2 truncate text-secondary-text group-data-[focus]:text-secondary-text">
											{option.sublabel}
										</span>
									)}
								</div>

								<span className="absolute inset-y-0 right-0 hidden items-center pr-4 text-primary-icon group-data-[selected]:flex group-data-[focus]:text-primary-icon">
									<IconSvg icon="check" />
								</span>
							</ComboboxOption>
						))}
					</ComboboxOptions>
				)}
			</div>
		</Combobox>
	)
}

export default memo(SelectSearchBox)
